<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { useMutation, useQuery } from '@vue/apollo-composable'
import {
  EbomPartAllocationSuggestionDecision,
  EbomPartDetailsAcceptSuggestionMutation,
  EbomPartDetailsAcceptSuggestionMutationVariables,
  EbomPartDetailsAllocateEbomPartToStepMutation,
  EbomPartDetailsAllocateEbomPartToStepMutationVariables,
  EbomPartDetailsEbomPartQuery,
  EbomPartDetailsEbomPartQueryVariables,
  EbomPartDetailsModulesAndStepsQuery,
  EbomPartDetailsModulesAndStepsQueryVariables,
  EbomPartDetailsPromoteToArticlesMutation,
  EbomPartDetailsPromoteToArticlesMutationVariables,
  EbomPartDetailsRejectSuggestionMutation,
  EbomPartDetailsRejectSuggestionMutationVariables,
  EbomPartDetailsSnoozesMutation,
  EbomPartDetailsSnoozesMutationVariables,
  EbomPartDetailsUnsnoozeMutation,
  EbomPartDetailsUnsnoozeMutationVariables,
  EbomPartSnoozeStatus,
  PermissionOrganization,
} from '@/generated/graphql'
import gql from 'graphql-tag'
import { computed, reactive, ref, watch } from 'vue'
import { SelectOption } from '../input/SelectField.vue'
import SelectField from '@/components/input/SelectField.vue'
import useVuelidate from '@vuelidate/core'
import { minValue, required } from '@/validation'
import NotificationSaved from '@/components/notifications/NotificationSaved.vue'
import NotificationDeleted from '@/components/notifications/NotificationDeleted.vue'
import StepPicker from '@/components/input/StepPicker.vue'
import AllocationQuantityEditor from '@/components/article/AllocationQuantityEditor.vue'
import ArticleQuantity from '@/components/article/ArticleQuantity.vue'
import ArticleQuantityField from '@/components/input/ArticleQuantityField.vue'
import AsyncConfirmDialog from '@/components/dialogs/AsyncConfirmDialog.vue'
import Cross3dModelWarningDialog from '@/components/ebom/Cross3dModelWarningDialog.vue'
import { checkCross3dModelConflict } from '@/composables/useCross3dModelCheck'
import { useSetStepPreferred3dModel } from '@/composables/useSetStepPreferred3dModel'
import { ComponentExposed } from 'vue-component-type-helpers'
import useAuthorization from '@/composables/useAuthorization'
import IconEbomPartSnoozeStatus from '@/components/icons/IconEbomPartSnoozeStatus.vue'

const props = defineProps<{
  bopId: string
  ebomPartId: string
}>()

type LocalModule = EbomPartDetailsModulesAndStepsQuery['product']['bop']['nodes'][0]['module']
type LocalEbomPart = EbomPartDetailsEbomPartQuery['article']['ebomPart']

const { t } = useI18n()
const { hasOrgAuthz, reactiveOrgAuthz } = useAuthorization()

const modulesAndStepsFetchQuery = useQuery<
  EbomPartDetailsModulesAndStepsQuery,
  EbomPartDetailsModulesAndStepsQueryVariables
>(
  gql`
    query EbomPartDetailsModulesAndSteps($bopId: ID!) {
      product {
        bop(id: $bopId) {
          nodes {
            module {
              id
              name
              nodes {
                step {
                  id
                  name
                }
              }
            }
          }
        }
      }
    }
  `,
  () => ({
    bopId: props.bopId,
  }),
)
const modules = computed(
  () => modulesAndStepsFetchQuery.result.value?.product.bop.nodes.map((n) => n.module) || [],
)
const stepToModule = computed(() => {
  let map: { [key: string]: LocalModule } = {}

  modules.value.forEach((m) => {
    m.nodes.forEach((n) => {
      map[n.step.id] = m
    })
  })

  return map
})

const ebomPartFetchQuery = useQuery<
  EbomPartDetailsEbomPartQuery,
  EbomPartDetailsEbomPartQueryVariables
>(
  gql`
    query EbomPartDetailsEbomPart($ebomPartId: ID!) {
      article {
        ebomPart(id: $ebomPartId) {
          id
          ebomId
          articleNumber
          revision
          name
          milliQuantity
          unit
          snoozeStatus
          position
          article {
            id
          }
          stepAllocations {
            milliQuantity
            step {
              id
              name
            }
          }
          stepAllocationSuggestions {
            id
            source
            decision
            milliQuantity
            step {
              id
              name
            }
          }
        }
      }
    }
  `,
  () => ({
    ebomPartId: props.ebomPartId,
  }),
)
const ebomPart = computed<LocalEbomPart | undefined>(
  () => ebomPartFetchQuery.result.value?.article.ebomPart,
)
const isPromoted = computed(() => !!ebomPart.value?.article)
const isSnoozed = computed(() => ebomPart.value?.snoozeStatus != EbomPartSnoozeStatus.None)
const isAllocatable = computed(
  () => isPromoted.value && reactiveOrgAuthz(PermissionOrganization.OrgProductWrite).value,
)
const stepAllocations = computed(() => ebomPart.value?.stepAllocations || [])
const stepAllocationSuggestions = computed(
  () =>
    ebomPart.value?.stepAllocationSuggestions.filter(
      (s) => s.decision == EbomPartAllocationSuggestionDecision.Undecided,
    ) || [],
)
const totalMilliQuantityAllocated = computed(() =>
  stepAllocations.value.reduce((p, c) => p + c.milliQuantity, 0),
)
const netMilliQuantityAllocated = computed(
  () => totalMilliQuantityAllocated.value - (ebomPart.value?.milliQuantity || 0),
)

const notifySaved = ref(false)

const promoteToArticleMutation = useMutation<
  EbomPartDetailsPromoteToArticlesMutation,
  EbomPartDetailsPromoteToArticlesMutationVariables
>(
  gql`
    mutation EbomPartDetailsPromoteToArticles($id: ID!) {
      article {
        promoteEbomPartsToArticles(ids: [$id]) {
          id
        }
      }
    }
  `,
  { refetchQueries: ['EbomPartTreeEbomParts'] },
)
const confirmPromoteDialog = ref<ComponentExposed<typeof AsyncConfirmDialog>>()
const promoteToArticle = async () => {
  await confirmPromoteDialog.value?.open(
    () =>
      promoteToArticleMutation.mutate({ id: props.ebomPartId }).then(() => {
        ebomPartFetchQuery.refetch()
      }),
    undefined,
  )
}

type Model = {
  module?: LocalModule
  stepId?: string
  milliQuantity: number
}
const model = reactive<Model>({
  milliQuantity: 1000,
})
const validation = useVuelidate<Model>(
  {
    module: { required },
    stepId: { required },
    milliQuantity: { required, min: minValue(1) },
  },
  model,
)
watch(
  () => props.ebomPartId,
  () => {
    model.module = undefined
    model.stepId = undefined
    model.milliQuantity = 1000
  },
)
watch(
  () => model.module,
  () => {
    model.stepId = undefined
    model.milliQuantity = 1000
    validation.value.$reset()
  },
)
watch(
  () => model.stepId,
  () => {
    model.milliQuantity = 1000
    validation.value.$reset()
  },
)

const moduleOptions = computed<SelectOption<LocalModule>[]>(() =>
  modules.value.map((m) => ({
    title: m.name,
    value: m,
  })),
)

const allocateNewMutation = useMutation<
  EbomPartDetailsAllocateEbomPartToStepMutation,
  EbomPartDetailsAllocateEbomPartToStepMutationVariables
>(
  gql`
    mutation EbomPartDetailsAllocateEbomPartToStep($command: AllocateEbomPartToStepCommand!) {
      article {
        allocateEbomPartToStep(command: $command)
      }
    }
  `,
  {
    refetchQueries: ['EbomPartTreeEbomParts', 'EbomDiffTreeData', 'BopStepViewerData'],
  },
)
const setStepPreferred3dModel = useSetStepPreferred3dModel()
const warningDialog = ref<ComponentExposed<typeof Cross3dModelWarningDialog>>()

async function allocateNew() {
  validation.value.$touch()
  if (validation.value.$invalid || !isAllocatable.value) {
    return
  }

  const ebomPartId = ebomPart.value?.id as string
  const ebomId = ebomPart.value?.ebomId as string
  const stepId = model.stepId as string

  let chosenBopUploadId: string | undefined
  const check = await checkCross3dModelConflict({
    stepId,
    bopId: props.bopId,
    newEbomIds: [ebomId],
  })
  if (check.conflict) {
    chosenBopUploadId = await warningDialog.value?.open({
      currentEbomName: check.currentEbomName,
      newEbomName: check.newEbomName,
      currentBopUploadId: check.currentBopUploadId,
      newBopUploadId: check.newBopUploadId,
    })
    if (!chosenBopUploadId) return
  }

  await allocateNewMutation.mutate({
    command: {
      ebomPartId,
      stepId,
      milliQuantity: model.milliQuantity,
    },
  })
  if (chosenBopUploadId) {
    await setStepPreferred3dModel(stepId, chosenBopUploadId)
  }
  notifySaved.value = true
  ebomPartFetchQuery.refetch()
  model.module = undefined
  model.stepId = undefined
  model.milliQuantity = 1000
  validation.value.$reset()
}

const acceptSuggestionMutation = useMutation<
  EbomPartDetailsAcceptSuggestionMutation,
  EbomPartDetailsAcceptSuggestionMutationVariables
>(
  gql`
    mutation EbomPartDetailsAcceptSuggestion($command: AcceptEbomPartAllocationSuggestionCommand!) {
      article {
        acceptEbomPartAllocationSuggestion(command: $command) {
          decision
        }
      }
    }
  `,
  {
    refetchQueries: ['EbomPartTreeEbomParts'],
  },
)
function acceptSuggestion(id: string, milliQuantity: number) {
  acceptSuggestionMutation
    .mutate({
      command: {
        id,
        milliQuantity,
      },
    })
    .then(() => {
      notifySaved.value = true
      ebomPartFetchQuery.refetch()
    })
}

const rejectSuggestionMutation = useMutation<
  EbomPartDetailsRejectSuggestionMutation,
  EbomPartDetailsRejectSuggestionMutationVariables
>(
  gql`
    mutation EbomPartDetailsRejectSuggestion($id: ID!) {
      article {
        rejectEbomPartAllocationSuggestion(id: $id) {
          decision
        }
      }
    }
  `,
  {
    refetchQueries: ['EbomPartTreeEbomParts'],
  },
)
function rejectSuggestion(id: string) {
  rejectSuggestionMutation.mutate({ id }).then(() => {
    notifySaved.value = true
    ebomPartFetchQuery.refetch()
  })
}

function updateAllocationQuantity(stepId: string, milliQuantity: number) {
  allocateNewMutation
    .mutate({
      command: {
        ebomPartId: props.ebomPartId,
        stepId,
        milliQuantity,
      },
    })
    .then(() => {
      notifySaved.value = true
      ebomPartFetchQuery.refetch()
    })
}

const notifyDeleted = ref(false)
function deleteAllocation(stepId: string, ebomPartId: string) {
  allocateNewMutation
    .mutate({
      command: {
        stepId,
        ebomPartId,
        milliQuantity: 0,
      },
    })
    .then(() => {
      notifyDeleted.value = true
      ebomPartFetchQuery.refetch()
    })
}

const snoozeMutation = useMutation<
  EbomPartDetailsSnoozesMutation,
  EbomPartDetailsSnoozesMutationVariables
>(
  gql`
    mutation EbomPartDetailsSnoozes($id: ID!) {
      article {
        snoozeEbomParts(ebomPartIds: [$id])
      }
    }
  `,
  {
    refetchQueries: ['EbomPartTreeEbomParts'],
  },
)
const snooze = () => {
  snoozeMutation.mutate({ id: ebomPart.value?.id as string }).then(() => {
    notifySaved.value = true
    ebomPartFetchQuery.refetch()
  })
}

const unsnoozeMutation = useMutation<
  EbomPartDetailsUnsnoozeMutation,
  EbomPartDetailsUnsnoozeMutationVariables
>(
  gql`
    mutation EbomPartDetailsUnsnooze($id: ID!) {
      article {
        unsnoozeEbomParts(ebomPartIds: [$id])
      }
    }
  `,
  {
    refetchQueries: ['EbomPartTreeEbomParts'],
  },
)
const unsnooze = () => {
  unsnoozeMutation.mutate({ id: ebomPart.value?.id as string }).then(() => {
    notifySaved.value = true
    ebomPartFetchQuery.refetch()
  })
}
</script>

<template>
  <h3>{{ ebomPart?.articleNumber }}</h3>

  <v-table density="compact" style="vertical-align: top">
    <tbody>
      <tr>
        <td style="width: 18em">{{ t('entity.ebomPart.field.name') }}</td>
        <td>{{ ebomPart?.name }}</td>
      </tr>
      <tr>
        <td>{{ t('entity.ebomPart.field.revision') }}</td>
        <td>{{ ebomPart?.revision }}</td>
      </tr>
      <tr v-if="ebomPart?.position">
        <td>{{ t('entity.ebomPart.field.position') }}</td>
        <td>{{ ebomPart?.position }}</td>
      </tr>
      <tr>
        <td>
          {{ t('entity.ebomPart.isPromoted') }}
        </td>
        <td>
          <v-icon :icon="!!ebomPart?.article ? 'check' : 'highlight_off'" />
          <v-btn
            v-if="!isPromoted && hasOrgAuthz(PermissionOrganization.OrgArticleWrite)"
            color="primary"
            variant="elevated"
            density="compact"
            class="ml-5"
            :loading="promoteToArticleMutation.loading.value"
            @click="promoteToArticle"
          >
            {{ t('component.ebomPartDetails.buttonPromoteEbomPart') }}
          </v-btn>
        </td>
      </tr>
      <tr>
        <td>
          {{ t('entity.ebomPart.field.snoozeStatus') }}
        </td>
        <td>
          <icon-ebom-part-snooze-status
            :status="ebomPart?.snoozeStatus || EbomPartSnoozeStatus.None"
          />
          <v-btn
            v-if="!isSnoozed && hasOrgAuthz(PermissionOrganization.OrgEbomWrite)"
            color="primary"
            variant="elevated"
            density="compact"
            class="ml-5"
            :loading="snoozeMutation.loading.value"
            @click="snooze"
          >
            {{ t('component.ebomPartDetails.buttonSnoozeEbomPart') }}
          </v-btn>
          <v-btn
            v-if="isSnoozed && hasOrgAuthz(PermissionOrganization.OrgEbomWrite)"
            color="primary"
            variant="elevated"
            density="compact"
            class="ml-5"
            :loading="unsnoozeMutation.loading.value"
            @click="unsnooze"
          >
            {{ t('component.ebomPartDetails.buttonUnSnoozeEbomPart') }}
          </v-btn>
        </td>
      </tr>
      <tr>
        <td>
          {{ t('entity.ebomPart.field.quantity') }}
        </td>
        <td>
          <article-quantity :milli-quantity="ebomPart?.milliQuantity" :unit="ebomPart?.unit" />
        </td>
      </tr>
      <tr v-if="isAllocatable">
        <td>
          {{ t('component.ebomPartDetails.quantityAllocatedToSteps') }}
        </td>
        <td>
          <article-quantity :milli-quantity="totalMilliQuantityAllocated" :unit="ebomPart?.unit" />

          <v-chip
            density="comfortable"
            :color="netMilliQuantityAllocated == 0 ? 'success' : 'error'"
            class="ml-2"
          >
            <article-quantity :milli-quantity="netMilliQuantityAllocated" :unit="ebomPart?.unit" />
          </v-chip>
        </td>
      </tr>
    </tbody>
  </v-table>

  <h3 class="mt-5">
    {{ t('component.ebomPartDetails.headerStepAllocations') }}
  </h3>
  <v-table density="compact">
    <thead>
      <tr>
        <th style="width: 35%">{{ t('component.ebomPartDetails.tableHeaderModule') }}</th>
        <th style="width: 40%">{{ t('component.ebomPartDetails.tableHeaderStep') }}</th>
        <th style="width: 25%">{{ t('component.ebomPartDetails.tableHeaderQuantity') }}</th>
      </tr>
    </thead>
    <tbody>
      <tr v-if="isAllocatable">
        <td>
          <select-field
            v-model="model.module"
            :items="moduleOptions"
            density="compact"
            :validation="validation.module"
          />
        </td>
        <td>
          <step-picker
            v-model="model.stepId"
            no-label
            :module-id="model.module?.id"
            density="compact"
            :validation="validation.step"
          />
        </td>
        <td>
          <article-quantity-field
            v-model="model.milliQuantity"
            :validation="validation.milliQuantity"
            :unit="ebomPart?.unit"
            density="compact"
          >
            <template #append>
              <v-btn
                density="compact"
                variant="flat"
                icon="add"
                color="primary"
                :disabled="validation.$invalid"
                :loading="allocateNewMutation.loading.value"
                @click="allocateNew()"
              />
            </template>
          </article-quantity-field>
        </td>
      </tr>
      <template v-for="s in stepAllocationSuggestions" :key="s.id">
        <tr class="bg-grey-lighten-4">
          <td class="suggestion-module">
            <router-link
              v-if="!!stepToModule[s.step.id]"
              :to="{ name: 'module', params: { moduleId: stepToModule[s.step.id].id } }"
            >
              {{ stepToModule[s.step.id]?.name }}
            </router-link>
            <v-chip density="compact" class="float-right">
              {{ t('component.ebomPartDetails.labelSuggestion') }}
            </v-chip>
          </td>
          <td>
            <router-link
              v-if="!!stepToModule[s.step.id]"
              :to="{
                name: 'module',
                params: { moduleId: stepToModule[s.step.id].id, nodeId: s.step.id },
              }"
            >
              {{ s.step.name }}
            </router-link>
          </td>
          <td>
            <article-quantity :milli-quantity="s.milliQuantity" :unit="ebomPart?.unit" />
            <v-btn
              class="float-right"
              variant="text"
              density="compact"
              icon="thumb_down"
              :loading="allocateNewMutation.loading.value"
              @click="rejectSuggestion(s.id)"
            />
            <v-btn
              class="float-right mr-1"
              variant="text"
              density="compact"
              icon="thumb_up"
              :loading="allocateNewMutation.loading.value"
              @click="acceptSuggestion(s.id, s.milliQuantity)"
            />
          </td>
        </tr>
      </template>
      <tr v-for="a in stepAllocations" :key="a.step.id">
        <td>
          <router-link
            v-if="!!stepToModule[a.step.id]"
            :to="{ name: 'module', params: { moduleId: stepToModule[a.step.id].id } }"
          >
            {{ stepToModule[a.step.id]?.name }}
          </router-link>
        </td>
        <td>
          <router-link
            v-if="!!stepToModule[a.step.id]"
            :to="{
              name: 'module',
              params: { moduleId: stepToModule[a.step.id].id, nodeId: a.step.id },
            }"
          >
            {{ a.step.name }}
          </router-link>
        </td>
        <td>
          <div class="d-flex align-center">
            <allocation-quantity-editor
              v-if="isAllocatable"
              :milli-quantity="a.milliQuantity"
              :unit="ebomPart?.unit"
              :loading="allocateNewMutation.loading.value"
              @change="(milliQuantity) => updateAllocationQuantity(a.step.id, milliQuantity)"
            />
            <article-quantity v-else :milli-quantity="a.milliQuantity" :unit="ebomPart?.unit" />
            <v-btn
              class="ml-auto"
              variant="text"
              density="compact"
              icon="delete"
              :loading="allocateNewMutation.loading.value"
              @click="deleteAllocation(a.step.id, props.ebomPartId)"
            />
          </div>
        </td>
      </tr>
    </tbody>
  </v-table>

  <async-confirm-dialog ref="confirmPromoteDialog">
    <template #default>
      {{ t('component.ebomPartDetails.promoteDialogContent') }}
    </template>
  </async-confirm-dialog>

  <cross3d-model-warning-dialog ref="warningDialog" />

  <notification-saved v-model="notifySaved" />
  <notification-deleted v-model="notifyDeleted" />
</template>

<style scoped lang="scss"></style>
